/**
 * ViewOverrideList -- displays user-authored view-override notes for a symbol.
 *
 * Fetches notes from /v1/client-notes?topic=view-override&subject=<symbol>
 * and renders them below the thesis section on the position detail screen.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { View, Text, StyleSheet, ActivityIndicator } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { API_CONFIG } from '../../config/api';
import { getToken } from '../../services/tokenStorage';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ViewNote {
  id: string;
  content_md: string;
  updated_at: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

async function authHeaders(): Promise<HeadersInit> {
  const token = await getToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function ViewOverrideList({ symbol }: { symbol: string }) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [notes, setNotes] = useState<ViewNote[]>([]);
  const [loading, setLoading] = useState(true);
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  useEffect(() => {
    let cancelled = false;

    const fetchNotes = async () => {
      try {
        setLoading(true);
        const resp = await fetch(
          `${API_CONFIG.baseURL}/v1/client-notes?topic=view-override&subject=${encodeURIComponent(symbol)}`,
          { headers: await authHeaders() }
        );
        if (!resp.ok) {
          setNotes([]);
          return;
        }
        const data: ViewNote[] = await resp.json();
        if (!cancelled) setNotes(data);
      } catch {
        if (!cancelled) setNotes([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchNotes();
    return () => {
      cancelled = true;
    };
  }, [symbol]);

  if (loading) {
    return <ActivityIndicator size="small" color={theme.colors.primary} />;
  }

  if (notes.length === 0) {
    return null;
  }

  return (
    <View style={styles.container}>
      <Text style={styles.label}>{t('portfolio.yourView', { defaultValue: 'Your view' })}</Text>
      {notes.map((n) => (
        <View key={n.id} style={styles.noteCard}>
          <Text style={styles.noteText}>{n.content_md}</Text>
          <Text style={styles.noteDate}>{n.updated_at.slice(0, 10)}</Text>
        </View>
      ))}
    </View>
  );
}

/* ------------------------------------------------------------------ */
/*  Styles                                                             */
/* ------------------------------------------------------------------ */

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      marginTop: theme.spacing.md,
      paddingHorizontal: theme.spacing.md,
    },
    label: {
      color: theme.colors.muted,
      fontSize: 12,
      marginBottom: 4,
    },
    noteCard: {
      padding: 8,
      borderLeftWidth: 2,
      borderLeftColor: theme.colors.primary,
      marginBottom: 4,
    },
    noteText: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeSm,
    },
    noteDate: {
      color: theme.colors.muted,
      fontSize: 10,
      marginTop: 2,
    },
  });
}
